/**
 * Type Mappings Utility
 * Derives task types and response types from /api/v1/subjects API.
 * API subject shape: { subjectCode, subjectName, years, countries, states, taskTypes: [{ taskType, responseTypes: [] }] }
 *
 * Usage:
 * 1. Fetch subjects from API using getSubjectsFromClasses() from service.js
 * 2. Pass the apiSubjects array to these utility functions
 * 3. Filter by subject name(s), state, and year as needed
 */

const normalizeYear = (year) => {
  if (!year) return null;
  const yearStr = String(year).replace(/^Year-?\s*/i, '').trim();
  const n = parseInt(yearStr, 10);
  return Number.isNaN(n) ? null : n;
};

const normalizeSubject = (subject) => {
  if (!subject) return '';
  if (typeof subject === 'string') return subject;
  return subject.subjectName || subject.name || subject.title || subject.subjectCode || subject.code || '';
};

const subjectMatches = (apiSubject, inputSubject) => {
  if (!apiSubject || !inputSubject) return false;
  const input = normalizeSubject(inputSubject).toLowerCase().trim();
  const name = (apiSubject.subjectName || apiSubject.name || apiSubject.title || '').toLowerCase().trim();
  const code = (apiSubject.subjectCode || apiSubject.code || '').toLowerCase().trim();
  return name === input || code === input;
};

const yearMatches = (yearsArr, normalizedYear) => {
  if (normalizedYear == null || !Array.isArray(yearsArr) || !yearsArr.length) return true;
  return yearsArr.some((y) => {
    const ny = normalizeYear(y);
    return ny != null && ny === normalizedYear || y === normalizedYear;
  });
};

const matchesStateYear = (apiSubject, state, normalizedYear) => {
  const stateArr = Array.isArray(state) ? state : (state ? [state] : []);
  const stateNorms = stateArr.map((s) => s?.toUpperCase?.()?.trim()).filter(Boolean);
  if (stateNorms.length && apiSubject.states?.length && !apiSubject.states.some((s) => stateNorms.includes(String(s).toUpperCase()))) return false;
  if (normalizedYear != null && apiSubject.years?.length && !yearMatches(apiSubject.years, normalizedYear)) return false;
  return true;
};

/**
 * @param {Array} apiSubjects - subjects array from /api/v1/subjects
 * @param {string|Array<string>} subjectNames - Subject name(s) or code(s)
 * @param {string} state
 * @param {string} year
 */
export const getTaskTypesForSubjects = (apiSubjects, subjectNames, state = 'NSW', year = '11') => {
  if (!Array.isArray(apiSubjects) || !apiSubjects.length) return [];
  const subjectsArray = Array.isArray(subjectNames) ? subjectNames : (subjectNames ? [subjectNames] : []);
  const normalizedYear = normalizeYear(year);

  const result = [];
  const seen = new Set();
  for (const row of apiSubjects) {
    if (subjectsArray.length && !subjectsArray.some((s) => subjectMatches(row, s))) continue;
    if (!matchesStateYear(row, state, normalizedYear)) continue;
    for (const t of row.taskTypes || []) {
      const code = t.taskType || t;
      if (code && !seen.has(code)) {
        seen.add(code);
        result.push({ code, name: code, tooltip: '' });
      }
    }
  }
  return result.length ? result : [];
};

export const getTaskTypesForSubjectsWithYears = (apiSubjects, subjectNames, state, years = []) => {
  const arr = Array.isArray(years) ? years : (years != null ? [years] : []);
  const seen = new Set();
  const result = [];
  for (const year of arr) {
    const list = getTaskTypesForSubjects(apiSubjects, subjectNames, state, year);
    for (const t of list) {
      const code = t.code || t;
      if (code && !seen.has(code)) {
        seen.add(code);
        result.push({ code, name: t.name || code, tooltip: t.tooltip || '' });
      }
    }
  }
  return result;
};

export const getResponseTypesForSubjectsWithYears = (apiSubjects, subjectNames, taskTypes, state, years = []) => {
  const arr = Array.isArray(years) ? years : (years != null ? [years] : []);
  const seen = new Set();
  const result = [];
  for (const year of arr) {
    const list = getResponseTypesForSubjects(apiSubjects, subjectNames, taskTypes, state, year);
    for (const r of list) {
      const code = r.code;
      if (code && !seen.has(code)) {
        seen.add(code);
        result.push({
          code,
          name: r.name || code,
          tooltip: r.tooltip || '',
        });
      }
    }
  }
  return result;
};

/**
 * @param {Array} apiSubjects - subjects array from /api/v1/subjects
 * @param {string|Array<string>} subjectNames
 * @param {string|Array<string>} taskTypes - one or more task types to filter by
 * @param {string} state
 * @param {string} year
 */
export const getResponseTypesForSubjects = (apiSubjects, subjectNames, taskTypes, state = 'NSW', year = '11') => {
  if (!Array.isArray(apiSubjects) || !apiSubjects.length) return [];
  const subjectsArray = Array.isArray(subjectNames) ? subjectNames : (subjectNames ? [subjectNames] : []);
  const normalizedYear = normalizeYear(year);
  const taskTypeArray = Array.isArray(taskTypes) ? taskTypes : (taskTypes ? [taskTypes] : []);

  const result = [];
  const seen = new Set();
  for (const row of apiSubjects) {
    if (subjectsArray.length && !subjectsArray.some((s) => subjectMatches(row, s))) continue;
    if (!matchesStateYear(row, state, normalizedYear)) continue;

    // Get response types from all task types
    for (const t of row.taskTypes || []) {
      const currentTaskType = t.taskType || t;
      // If task type filters are provided, only include response types from matching task types
      if (taskTypeArray.length && !taskTypeArray.includes(currentTaskType)) continue;

      for (const r of t.responseTypes || []) {
        const code = typeof r === 'string' ? r : (r?.value ?? r?.code);
        if (code && !seen.has(code)) {
          seen.add(code);
          result.push({
            code,
            name: typeof r === 'string' ? r : (r?.label ?? r?.name ?? code),
            tooltip: typeof r === 'object' ? (r?.tooltip ?? '') : '',
          });
        }
      }
    }
  }
  return result;
};

/**
 * @param {Array} apiSubjects - subjects array from /api/v1/subjects
 */
export const getAllTaskTypeLabels = (apiSubjects = []) => {
  const labels = {};
  if (!Array.isArray(apiSubjects)) return labels;
  for (const row of apiSubjects) {
    for (const t of row.taskTypes || []) {
      const code = t.taskType || t;
      if (code && !labels[code]) labels[code] = code;
    }
  }
  return labels;
};

/**
 * @param {Array} apiSubjects - subjects array from /api/v1/subjects
 */
export const getAllResponseTypeLabels = (apiSubjects = []) => {
  const labels = {};
  if (!Array.isArray(apiSubjects)) return labels;
  for (const row of apiSubjects) {
    for (const t of row.taskTypes || []) {
      for (const r of t.responseTypes || []) {
        const code = typeof r === 'string' ? r : (r?.value ?? r?.code);
        if (code && !labels[code]) labels[code] = typeof r === 'string' ? r : (r?.label ?? r?.name ?? code);
      }
    }
  }
  return labels;
};

export const clearTypeCache = () => {};

/**
 * Get all unique task type codes from API subjects
 */
export const getAllTaskTypes = (apiSubjects = []) => {
  const types = new Set();
  for (const row of apiSubjects || []) {
    for (const t of row.taskTypes || []) {
      if (t.taskType || t) types.add(t.taskType || t);
    }
  }
  return Array.from(types);
};

/**
 * Get all unique response type codes from API subjects
 */
export const getAllResponseTypes = (apiSubjects = []) => {
  const types = new Set();
  for (const row of apiSubjects || []) {
    for (const t of row.taskTypes || []) {
      for (const r of t.responseTypes || []) {
        const code = typeof r === 'string' ? r : (r?.value ?? r?.code);
        if (code) types.add(code);
      }
    }
  }
  return Array.from(types);
};

export default {
  getTaskTypesForSubjects,
  getResponseTypesForSubjects,
  getAllTaskTypeLabels,
  getAllResponseTypeLabels,
  getAllTaskTypes,
  getAllResponseTypes,
  clearTypeCache,
};
